// Presentation decks: the teacher's fullscreen view is one linear track of
// slides + the room's quiz questions, so a lesson never leaves the app for
// PowerPoint. Keyed by room code, same as lessons.js. A room with no deck
// here falls back to a generic one (cover + its questions + results).
//
// A quiz stop only carries `order` (the question's order_index); the real
// question payload comes from GET /api/rooms/:id at runtime.
//
// A stop may also carry `notes`: what the instructor says while that slide is
// up. It is shown on the control panel only and never reaches the projector,
// so it is the one field here written to be spoken rather than read. Leaving
// it out is fine — most slides speak for themselves.

const PYINIT = {
  meta: {
    unit: 'หน่วยที่ 1',
    title: 'การเตรียมความพร้อม\nและการเริ่มต้นใช้งานภาษาไพทอน',
    subtitle: 'การติดตั้งสภาพแวดล้อม และเครื่องมือพัฒนาโปรแกรม (IDE)',
    instructor: 'นายกฤตนัย บุญน้อย',
  },
  stops: [
    { kind: 'cover' },

    {
      kind: 'objectives',
      title: 'เป้าหมายการเรียนรู้ในวันนี้',
      items: [
        {
          icon: 'gear',
          head: 'ติดตั้งระบบได้',
          body: 'บอกขั้นตอนการดาวน์โหลด ติดตั้ง และตั้งค่าระบบพื้นฐาน\nสำหรับเขียนโปรแกรมภาษาไพทอนได้อย่างถูกต้อง',
        },
        {
          icon: 'monitor',
          head: 'เข้าใจเครื่องมือ',
          body: 'เข้าใจภาพรวมและบอกหน้าที่ของเครื่องมือเขียนโค้ด (IDE/Editor) ที่ใช้สำหรับการพัฒนาโปรแกรมได้',
        },
      ],
    },

    { kind: 'hand_demo', title: 'พลังของ Python' },

    { kind: 'quiz', order: 0 }, // poll: have you ever installed software yourself

    {
      kind: 'overview',
      kicker: 'ภาพรวมบทเรียน',
      title: '4 ขั้นตอนก่อนเริ่มเขียนโค้ด',
      steps: [
        { n: 1, icon: 'download', label: 'ดาวน์โหลด', note: 'โหลดตัวติดตั้งจากเว็บทางการ', hint: 'python.org' },
        { n: 2, icon: 'gear', label: 'ติดตั้ง & ตั้งค่า', note: 'สำคัญ! ติ๊ก Add Python to PATH', warn: true, hint: 'Add to PATH' },
        { n: 3, icon: 'terminal', label: 'ตรวจสอบ', note: 'เช็กเวอร์ชันใน Command Prompt', hint: 'python --version' },
        { n: 4, icon: 'code', label: 'ติดตั้ง IDE', note: 'ติดตั้งโปรแกรม Thonny ช่วยเขียนโค้ด', hint: 'Thonny' },
      ],
    },

    {
      kind: 'step',
      n: 1,
      title: 'ดาวน์โหลดตัวติดตั้ง',
      points: [
        { icon: 'globe', head: 'เว็บไซต์ทางการ', body: 'ไปที่ python.org/downloads' },
        { icon: 'monitor', head: 'เลือกระบบปฏิบัติการ', body: 'ดาวน์โหลดให้ตรงกับเครื่องที่ใช้ (Windows หรือ macOS)' },
        { icon: 'shield', head: 'ข้อควรระวัง', body: 'ควรดาวน์โหลดจากแหล่งที่น่าเชื่อถือเพื่อป้องกันมัลแวร์', warn: true },
      ],
      aside: { type: 'image', src: '/images/python-org-downloads.png', frame: 'browser', url: 'python.org/downloads' },
    },

    { kind: 'quiz', order: 1 }, // mc: the official download site

    {
      kind: 'step',
      n: 2,
      title: 'ติดตั้งและตั้งค่า',
      badge: 'ห้ามพลาด!',
      warn: true,
      headline: 'Add python.exe to PATH',
      lead: 'เมื่อเปิดโปรแกรมติดตั้งขึ้นมา ขั้นตอนที่สำคัญที่สุดคือการติ๊กกล่องข้อความนี้ก่อนกด Install Now',
      callout: {
        head: 'ทำไมถึงสำคัญ?',
        body: 'ระบบปฏิบัติการจะสามารถค้นหาและรู้จักคำสั่ง python ได้จากทุกโฟลเดอร์ในเครื่อง หากลืมติ๊ก คอมพิวเตอร์จะไม่รู้จักคำสั่งรันโปรแกรม',
      },
      aside: { type: 'installer' },
    },

    { kind: 'quiz', order: 2 }, // checkbox_sim: tick Add python.exe to PATH

    {
      kind: 'step',
      n: 3,
      title: 'ตรวจสอบความพร้อมของระบบ',
      points: [
        { icon: 'terminal', head: 'เปิดหน้าต่างพิมพ์คำสั่ง (Terminal)', body: 'Windows เปิด Command Prompt (พิมพ์ cmd ในช่องค้นหา) · macOS เปิด Terminal (กด Cmd + Space พิมพ์ Terminal)' },
        { icon: 'check', head: 'ตรวจสอบเวอร์ชัน', body: 'พิมพ์คำสั่งด้านล่าง หากติดตั้งสำเร็จ ระบบจะแสดงเวอร์ชันที่ติดตั้งไว้' },
      ],
      aside: { type: 'terminal', lines: [['in', 'python --version'], ['out', 'Python 3.14.6']] },
    },

    { kind: 'divider', part: 'ตอนที่ 2', title: 'เครื่องมือเขียนโค้ด' },

    {
      kind: 'step',
      n: 4,
      title: 'ติดตั้งเครื่องมือเขียนโค้ด (IDE)',
      headline: 'ทำไมต้องใช้ Thonny?',
      points: [
        { icon: 'rocket', head: 'ออกแบบมาเพื่อผู้เริ่มต้นโดยเฉพาะ' },
        { icon: 'cpu', head: 'มีตัวแปลภาษา (Interpreter) มาให้ในตัว' },
        { icon: 'flame', head: 'ใช้งานฟรี และมีขนาดเล็กมาก' },
        { icon: 'bug', head: 'ดูการทำงานของโค้ดทีละบรรทัดได้ง่าย (Debugging)' },
      ],
      aside: { type: 'image', src: '/images/thonny-org.png', frame: 'browser', url: 'thonny.org' },
    },

    { kind: 'quiz', order: 3 }, // mc: IDE = Thonny

    {
      kind: 'pipeline',
      kicker: 'การทำงานของระบบ',
      title: 'โครงสร้างการทำงาน โค้ดกลายเป็นผลลัพธ์ได้อย่างไร?',
      connectors: ['ส่งโค้ด', 'ประมวลผล'],
      stages: [
        { n: 1, icon: 'keyboard', label: 'ผู้ใช้ (Thonny)', body: 'เราพิมพ์โค้ด (Source Code) ลงในหน้าต่าง Editor ของโปรแกรม Thonny', role: 'Editor (พิมพ์โค้ด)' },
        { n: 2, icon: 'cpu', label: 'ตัวแปลภาษา', body: 'Interpreter รับโค้ดไปแปลความหมาย และสั่งคอมพิวเตอร์ให้ประมวลผล', role: 'Interpreter (สมองกล)' },
        { n: 3, icon: 'terminal', label: 'ผลลัพธ์ (Shell)', body: 'ส่งผลลัพธ์ที่ประมวลผลเสร็จแล้ว กลับมาแสดงให้เราเห็นในหน้าต่าง Shell', role: 'Shell (แสดงผล)' },
      ],
    },

    { kind: 'quiz', order: 5 }, // match: put the three stages in order
    { kind: 'quiz', order: 4 }, // order: recap the four install steps

    {
      kind: 'glossary',
      title: 'สรุปบทเรียน',
      terms: [
        { icon: 'code', term: 'Python', def: 'ภาษาที่ต้องติดตั้งระบบเพื่อให้คอมพิวเตอร์เข้าใจคำสั่ง' },
        { icon: 'gear', term: 'PATH', def: 'การตั้งค่าสำคัญเพื่อให้เรียกใช้ไพทอนได้จากทุกส่วนของเครื่อง' },
        { icon: 'terminal', term: 'Command Prompt / Terminal', def: 'ใช้สำหรับตรวจสอบความพร้อมของระบบ' },
        { icon: 'code', term: 'Thonny IDE', def: 'พื้นที่ทำงานที่ช่วยให้พิมพ์และแก้ไขโค้ดได้ง่ายขึ้น' },
        { icon: 'cpu', term: 'Interpreter', def: 'สมองกลที่ทำหน้าที่แปลโค้ดและประมวลผลตัวจริง' },
      ],
    },

    { kind: 'quiz', order: 6 }, // poll: one-sentence takeaway

    { kind: 'results' },
    { kind: 'outro', title: 'จบบทเรียน', body: 'ขอบคุณที่ตั้งใจเรียนวันนี้ — เจอกันใหม่ในบทเรียนถัดไป', credit: 'ไอคอนจากชุด Lucide · โลโก้ Python จาก python.org' },
  ],
};

const PYVAR = {
  meta: {
    unit: 'หน่วยที่ 2',
    title: 'การกำหนดตัวแปร\nและการเลือกใช้งานชนิดข้อมูลประเภทตัวเลข',
    subtitle: 'กฎการตั้งชื่อตัวแปร และการเลือกใช้ int / float ให้เหมาะกับข้อมูล',
    instructor: 'นายกฤตนัย บุญน้อย',
  },
  stops: [
    { kind: 'cover' },

    {
      kind: 'objectives',
      title: 'เป้าหมายการเรียนรู้ในวันนี้',
      items: [
        {
          icon: 'code',
          head: 'ตั้งชื่อตัวแปรได้ถูกต้อง',
          body: 'บอกความหมายของตัวแปร และกำหนดค่าให้ตัวแปรตามกฎการตั้งชื่อของภาษาไพทอนได้',
        },
        {
          icon: 'cpu',
          head: 'เลือกชนิดข้อมูลได้เหมาะสม',
          body: 'บอกชนิดข้อมูลจำนวนเต็ม (int) และจำนวนทศนิยม (float) ให้เหมาะสมกับข้อมูลที่กำหนดให้ได้',
        },
      ],
    },

    {
      // The lesson's opening, as the plan calls for it: one real transfer
      // slip on screen, learners guess what data type each number would be
      // stored as, then the teacher reveals it. An illustration, not a
      // scored question.
      kind: 'hook',
      kicker: 'ขั้นนำเข้าสู่บทเรียน',
      title: 'AI อ่านสลิปโอนเงิน แล้วกรอกลงฟอร์มให้อัตโนมัติ',
      slipImage: '/images/pyvar-transfer-slip.png',
      slipCaption: 'สลิปแบบนี้ทั้งหมด 3 ใบ ที่ AI ต้องอ่านในเซสชันนี้',
      guessPrompt: 'ถ้าโปรแกรมต้องอ่านตัวเลขจากสลิปนี้แล้วเก็บไว้ระหว่างทำงาน คิดว่าแต่ละตัวควรเก็บเป็นชนิดข้อมูลอะไร?',
      arrow: 'AI อ่านตัวเลขจากภาพ (OCR)',
      form: {
        head: 'โปรแกรมพักตัวเลขไว้ระหว่างทำงาน',
        note: 'ตัวเลขสองค่านี้มาจากคนละที่ และเก็บคนละชนิดข้อมูล',
        fields: [
          { label: 'ยอดเงินบนสลิป', value: '1250.75', type: 'float', note: 'มีเศษสตางค์ ต้องเก็บทศนิยม' },
          { label: 'จำนวนสลิปที่ AI อ่านได้', value: '3', type: 'int', note: 'นับเป็นใบ ไม่มีเศษ ต้องเป็นจำนวนเต็ม' },
        ],
      },
      warn: {
        head: 'ถ้าเลือกชนิดข้อมูลผิด เศษสตางค์หายทันที',
        body: 'เก็บยอดเงินเป็นจำนวนเต็ม (int) โปรแกรมจะตัดทศนิยมทิ้งให้เอง โดยไม่แจ้งข้อผิดพลาด',
        before: '1,250.75',
        after: '1,250',
        loss: 'หาย 75 สตางค์',
      },
    },

    {
      kind: 'step',
      n: 1,
      title: 'ตัวแปร (Variable) คืออะไร',
      points: [
        { icon: 'keyboard', head: 'ที่เก็บข้อมูลในหน่วยความจำ', body: 'ชื่อที่ใช้อ้างอิงพื้นที่จัดเก็บข้อมูลในหน่วยความจำ ที่เปลี่ยนแปลงค่าได้ระหว่างโปรแกรมทำงาน' },
        { icon: 'code', head: 'กำหนดค่าด้วยเครื่องหมาย =', body: 'Assignment Operator — นำค่าทางด้านขวามือไปเก็บไว้ในตัวแปรทางด้านซ้ายมือ' },
        { icon: 'check', head: 'ไม่ต้องประกาศชนิดข้อมูลล่วงหน้า', body: 'ภาษาไพทอนพิจารณาชนิดข้อมูลให้อัตโนมัติจากค่าที่กำหนดให้' },
      ],
      aside: { type: 'terminal', lines: [['in', 'score = 25'], ['in', 'print(score)'], ['out', '25']] },
    },

    { kind: 'quiz', order: 1 }, // mc: what score = 25 means

    {
      // All 5 rules on one screen, as a compact 5-card grid (3 top + 2
      // bottom, centered) instead of the old full-width point-list — a
      // vertical list of 5 full rows overflowed a 1280x720 projector by
      // ~230px, and shrinking the font to fit was ruled out (must stay
      // readable from the back of a room). The grid fits by keeping the
      // font size and instead trimming each rule to a terse example.
      kind: 'step',
      n: 2,
      title: '5 กฎการตั้งชื่อตัวแปร',
      grid: true,
      points: [
        { icon: 'code', head: 'ตัวอักษร ตัวเลข และ _ เท่านั้น', body: 'ห้าม ! @ # หรือเว้นวรรค' },
        { icon: 'shield', head: 'ห้ามขึ้นต้นด้วยตัวเลข', body: '2score ✗ → score2 ✓', warn: true },
        { icon: 'shield', head: 'ห้ามเว้นวรรค', body: 'total price ✗ → total_price ✓', warn: true },
        { icon: 'shield', head: 'ห้ามใช้คำสงวนของภาษา', body: 'if, for, while, class ✗', warn: true },
        { icon: 'check', head: 'ตัวพิมพ์เล็ก-ใหญ่ คนละตัวแปร', body: 'score ≠ Score' },
      ],
    },

    { kind: 'quiz', order: 2 }, // mc: which variable name follows the rules

    { kind: 'divider', part: 'ตอนที่ 2', title: 'ชนิดข้อมูลตัวเลข' },

    {
      kind: 'step',
      n: 3,
      title: 'จำนวนเต็ม (int) และจำนวนทศนิยม (float)',
      points: [
        { icon: 'cpu', head: 'int — จำนวนเต็ม', body: 'ตัวเลขที่ไม่มีจุดทศนิยม เช่น 5, 100, -3' },
        { icon: 'cpu', head: 'float — จำนวนทศนิยม', body: 'ตัวเลขที่มีจุดทศนิยม เช่น 5.0, 165.5, 1250.75' },
        { icon: 'terminal', head: 'ตรวจสอบด้วยคำสั่ง type()', body: 'บอกชนิดข้อมูลที่แท้จริงของตัวแปรนั้น ๆ' },
      ],
      aside: { type: 'terminal', lines: [['in', 'type(25)'], ['out', "<class 'int'>"], ['in', 'type(25.5)'], ['out', "<class 'float'>"]] },
    },

    { kind: 'quiz', order: 3 }, // mc: int or float for a single case

    {
      // Selection principle, shown as two examples students can watch play
      // out rather than read as prose: a count animates up one-by-one
      // (int — you can't have half a person), a measurement fills a gauge to
      // a decimal reading (float — a ruler always has a fraction). Each
      // plays once on arrival and holds on the final frame; "เล่นอีกครั้ง"
      // replays it on demand.
      kind: 'select-type',
      title: 'เลือกใช้ชนิดข้อมูลให้ถูก',
      columns: [
        {
          head: 'ข้อมูลจากการนับ → int',
          body: 'นับทีละหนึ่ง ไม่มีเศษ',
          demo: { type: 'count', total: 5, unit: 'คน', label: 'จำนวนนักศึกษาในห้อง' },
        },
        {
          head: 'ข้อมูลจากการวัด/มีหน่วยย่อย → float',
          body: 'วัดได้ละเอียดถึงทศนิยม',
          demo: { type: 'measure', value: 165.5, unit: 'ซม.', label: 'ส่วนสูงของนักศึกษา' },
        },
      ],
    },

    { kind: 'quiz', order: 4 }, // classify: int or float for five items

    {
      kind: 'glossary',
      title: 'สรุปบทเรียน',
      terms: [
        { icon: 'keyboard', term: 'ตัวแปร (Variable)', def: 'ชื่อที่ใช้อ้างอิงพื้นที่เก็บข้อมูลในหน่วยความจำ ที่เปลี่ยนค่าได้' },
        { icon: 'code', term: 'Assignment Operator (=)', def: 'เครื่องหมายกำหนดค่า นำค่าขวามือไปเก็บในตัวแปรซ้ายมือ' },
        { icon: 'cpu', term: 'int', def: 'ชนิดข้อมูลจำนวนเต็ม ไม่มีจุดทศนิยม' },
        { icon: 'cpu', term: 'float', def: 'ชนิดข้อมูลจำนวนทศนิยม มีจุดทศนิยม' },
        { icon: 'terminal', term: 'type()', def: 'คำสั่งตรวจสอบชนิดข้อมูลของตัวแปร' },
        { icon: 'shield', term: 'Reserved Words', def: 'คำสงวนของภาษา (เช่น if, for, while) ใช้ตั้งชื่อตัวแปรไม่ได้' },
      ],
    },

    { kind: 'quiz', order: 5 }, // poll: one-sentence takeaway

    { kind: 'results' },
    { kind: 'outro', title: 'จบบทเรียน', body: 'ขอบคุณที่ตั้งใจเรียนวันนี้ — เจอกันใหม่ในบทเรียนถัดไป', credit: 'ไอคอนจากชุด Lucide' },
  ],
};

const PYOP = {
  meta: {
    unit: 'หน่วยที่ 3',
    title: 'ใช้งานตัวดำเนินการ\nเพื่อประมวลผล คำนวณ และจัดการข้อมูลพื้นฐาน',
    subtitle: 'ตัวดำเนินการทางคณิตศาสตร์ ลำดับความสำคัญ และการกำหนดค่าแบบย่อ',
    instructor: 'นายกฤตนัย บุญน้อย',
  },
  stops: [
    { kind: 'cover' },

    {
      kind: 'objectives',
      title: 'เป้าหมายการเรียนรู้ในวันนี้',
      items: [
        {
          icon: 'cpu',
          head: 'คำนวณได้ตามที่ต้องการ',
          body: 'บอกหน้าที่ของตัวดำเนินการทางคณิตศาสตร์ และเขียนนิพจน์คำนวณ\nให้ได้ผลลัพธ์ตามที่กำหนดได้',
        },
        {
          icon: 'zap',
          head: 'อ่านนิพจน์ยาวได้ถูกต้อง',
          body: 'บอกลำดับความสำคัญของตัวดำเนินการ และวิเคราะห์ผลลัพธ์\nของนิพจน์ที่มีหลายตัวดำเนินการได้',
        },
      ],
    },

    {
      // The lesson's opening, carrying on from unit 2's transfer slip with
      // the same amount. The teacher uncovers one answer per arrow press
      // (steps: 3), so the class guesses before seeing what Python says.
      kind: 'bill-split',
      steps: 3,
      kicker: 'ขั้นนำเข้าสู่บทเรียน',
      // Short enough to read in one glance from the back of the room: the
      // slide is the bill and the three answers arriving, not a paragraph.
      title: 'ค่าอาหาร 1,250 บาท หารกัน 3 คน',
      bill: {
        head: 'บิลมื้อนี้',
        total: '1,250',
        unit: 'บาท',
        people: 3,
      },
      results: [
        { op: '/', expr: '1250 / 3', value: '416.666...', tone: 'warn', note: 'จ่ายจริงไม่ได้' },
        { op: '//', expr: '1250 // 3', value: '416', tone: 'good', note: 'คนละเท่านี้' },
        { op: '%', expr: '1250 % 3', value: '2', tone: 'good', note: 'ยังเหลือ' },
      ],
      closing: 'เลขชุดเดียวกัน — เปลี่ยนตัวดำเนินการ ได้คนละคำตอบ',
    },

    { kind: 'quiz', order: 0 }, // poll: split the bill — where does the remainder go

    {
      // Four of the seven are arithmetic the class already has; the lesson is
      // the other three. They get the slide, one per arrow press, each
      // colouring the same seven units to show what it actually returns.
      kind: 'op-table',
      steps: 3,
      kicker: 'ขั้นที่ 1 · การคำนวณ',
      title: 'สามตัวที่ต้องแยกให้ออก',
      knownLabel: 'สี่ตัวนี้ เหมือนเลขที่เคยเรียนมา',
      known: [
        { op: '+', name: 'บวก', example: '7 + 2', result: '9' },
        { op: '-', name: 'ลบ', example: '7 - 2', result: '5' },
        { op: '*', name: 'คูณ', example: '7 * 2', result: '14' },
        { op: '**', name: 'ยกกำลัง', example: '7 ** 2', result: '49' },
      ],
      unitTotal: 7,
      unitPer: 2,
      focus: [
        { op: '/', name: 'หาร', expr: '7 / 2', value: '3.5', gloss: 'แบ่งจริง ได้ทศนิยมเสมอ', viz: 'split' },
        { op: '//', name: 'หารปัดลง', expr: '7 // 2', value: '3', gloss: 'ได้กี่กองเต็ม ๆ เศษทิ้งไป', viz: 'whole' },
        { op: '%', name: 'เศษจากการหาร', expr: '7 % 2', value: '1', gloss: 'เหลือเท่าไหร่ ไม่สนกี่กอง', viz: 'remainder' },
      ],
      closing: 'เลข 7 ชุดเดียวกัน แบ่งกองละ 2 เหมือนกัน — แต่ตอบคนละคำถาม',
    },

    { kind: 'quiz', order: 1 }, // mc: 1250 // 3 vs 1250 % 3
    { kind: 'quiz', order: 2 }, // expr: fill in four operator blanks

    { kind: 'divider', part: 'ตอนที่ 2', title: 'ลำดับความสำคัญของตัวดำเนินการ' },

    {
      // The heart of objective 2. The teacher walks it one step per arrow
      // press (steps: 3), talking through each one; the steps already taken
      // stay above so the class sees the whole chain.
      kind: 'calc-trace',
      steps: 3,
      kicker: 'ลองไล่ทีละขั้น',
      notes:
        'ก่อนกดขั้นแรก ให้ทั้งห้องทายผลลัพธ์ไว้ก่อน แล้วค่อยไล่ให้ดู\n'
        + 'พูดชื่อลำดับทุกขั้น: ยกกำลัง → คูณ → บวก ไม่ใช่แค่ชี้ตัวเลข\n'
        + 'ปิดท้ายด้วยกล่อง 400 — คนที่ไล่จากซ้ายไปขวาจะได้เลขนี้ ให้เขารู้ว่าผิดตรงไหน',
      title: 'นิพจน์เดียว คำนวณทีละขั้นจนเหลือค่าเดียว',
      expression: '2 + 3 * 4 ** 2',
      trace: [
        { tokens: ['2', '+', '3', '*', '4', '**', '2'], focus: [4, 5, 6], note: 'ยกกำลังทำก่อนเสมอ — 4 ** 2 ได้ 16' },
        { tokens: ['2', '+', '3', '*', '16'], focus: [2, 3, 4], note: 'คูณเป็นลำดับถัดมา — 3 * 16 ได้ 48' },
        { tokens: ['2', '+', '48'], focus: [0, 1, 2], note: 'บวกเป็นลำดับสุดท้าย — 2 + 48 ได้ 50' },
        { tokens: ['50'], focus: [0], note: 'ผลลัพธ์ของนิพจน์นี้คือ 50', final: true },
      ],
      wrong: {
        head: 'ถ้าไล่จากซ้ายไปขวาโดยไม่สนลำดับ',
        body: '2 + 3 = 5 → 5 * 4 = 20 → 20 ** 2 = 400 ซึ่งไม่ใช่คำตอบที่ไพทอนให้',
        value: '400',
      },
    },

    {
      kind: 'precedence',
      kicker: 'ขั้นที่ 2 · ลำดับความสำคัญ',
      notes:
        'ไม่ต้องให้ท่องสี่ชั้น ให้จำกฎเดียวพอ: ไม่แน่ใจเมื่อไหร่ ใส่วงเล็บ\n'
        + 'เน้นกฎซ้ายไปขวาให้ดี เพราะคำถามข้อถัดไป (10 / 2 * 3) วัดตรงนี้พอดี',
      title: 'ใครมาก่อน ใครมาหลัง',
      tiers: [
        { rank: 1, ops: ['( )'], label: 'วงเล็บ', note: 'บังคับให้ทำก่อนได้เสมอ ไม่ต้องจำลำดับ' },
        { rank: 2, ops: ['**'], label: 'ยกกำลัง' },
        // The four chips beside it already name the operators; the label
        // only has to say what they have in common, in one line.
        { rank: 3, ops: ['*', '/', '//', '%'], label: 'คูณและหารทุกแบบ' },
        { rank: 4, ops: ['+', '-'], label: 'บวก ลบ' },
      ],
      rules: [
        { head: 'ลำดับเท่ากัน ให้ทำจากซ้ายไปขวา', body: '8 - 3 + 1  →  (8 - 3) + 1  =  6' },
        { head: 'ไม่แน่ใจ ให้ใส่วงเล็บ', body: '(2 + 3) * 4  =  20' },
      ],
    },

    { kind: 'quiz', order: 3 }, // numeric: 10 / 2 * 3
    { kind: 'quiz', order: 4 }, // order: walk the calculation steps

    {
      kind: 'step',
      n: 3,
      title: 'กำหนดค่าแบบย่อ (Compound Assignment)',
      headline: 'บวกทับค่าเดิม เขียนสั้นลงได้',
      points: [
        { icon: 'code', head: 'score = score + 5  →  score += 5', body: 'เอาค่าเดิมของ score มาบวก 5 แล้วเก็บกลับเข้าตัวเดิม' },
        { icon: 'cpu', head: 'ใช้กับตัวอื่นได้เหมือนกัน', body: '-= *= /= //= %= ทำงานตามหลักเดียวกัน' },
      ],
      aside: {
        type: 'terminal',
        lines: [['in', 'score = 10'], ['in', 'score += 5'], ['in', 'print(score)'], ['out', '15']],
      },
    },

    {
      kind: 'glossary',
      title: 'สรุปบทเรียน',
      terms: [
        { icon: 'cpu', term: 'ตัวดำเนินการ (Operator)', def: 'สัญลักษณ์ที่สั่งให้นำข้อมูลไปประมวลผล เช่น + - * /' },
        { icon: 'code', term: 'นิพจน์ (Expression)', def: 'ค่ากับตัวดำเนินการที่ประกอบกันแล้วคำนวณออกมาได้ค่าเดียว' },
        { icon: 'terminal', term: '/  //  %', def: '/ ได้ทศนิยมเสมอ · // เอาผลหารจำนวนเต็ม · % เอาเศษที่เหลือ' },
        { icon: 'zap', term: 'ลำดับความสำคัญ', def: 'วงเล็บ → ยกกำลัง → คูณ/หาร → บวก/ลบ · ลำดับเท่ากันทำจากซ้ายไปขวา' },
        { icon: 'code', term: '+= (กำหนดค่าแบบย่อ)', def: 'นำค่าเดิมมาบวกเพิ่มแล้วเก็บกลับเข้าตัวแปรเดิม' },
      ],
    },

    { kind: 'quiz', order: 5 }, // poll: one-sentence takeaway

    { kind: 'results' },
    { kind: 'outro', title: 'จบบทเรียน', body: 'ขอบคุณที่ตั้งใจเรียนวันนี้ — เจอกันใหม่ในบทเรียนถัดไป', credit: 'ไอคอนจากชุด Lucide' },
  ],
};

// Unit 2 rebuilt for the final teaching session (2026-10-21). It runs in
// theme A ("ป้ายชื่อบนกระดาษ", meta.theme = 'tag'): a variable is a name tag
// tied to a value, ints are blue and floats orange wherever they appear, and
// the tag itself has no colour because the type belongs to the value.
const PYVAR2 = {
  meta: {
    unit: 'หน่วยที่ 2',
    title: 'การกำหนดตัวแปร\nและการเลือกใช้งานชนิดข้อมูลประเภทตัวเลข',
    subtitle: 'ตัวแปรคือป้ายชื่อที่ผูกกับค่า · นับได้ใช้ int วัดได้ใช้ float',
    instructor: 'นายกฤตนัย บุญน้อย',
    theme: 'tag',
  },
  stops: [
    {
      kind: 'cover',
      notes: '⏱ ก่อนเริ่มคาบ\nให้ผู้เรียนสแกน QR เข้าห้องให้ครบก่อน แล้วค่อยเริ่มจับเวลา 10 นาที',
    },

    {
      // The opening (steps: 2): the camera puts a name tag on each hand.
      // Counting first, then measuring, then the question it leaves open —
      // answered by the camera again at the start of part 2.
      kind: 'cam-tags',
      steps: 2,
      title: 'ป้ายชื่อติดมือ',
      question: 'ทำไม left ไม่เคยมีทศนิยม แต่ gap_cm มีตลอด?',
      notes:
        '⏱ 0:00–1:15 ขั้นนำ\n'
        + 'ยืนให้กล้องเห็นมือทั้งสองข้าง ชูนิ้วมือซ้าย 2 มือขวา 3 ให้ดู total = left + right เปลี่ยนตาม\n'
        + 'กดถัดไป: จีบนิ้วโป้งกับนิ้วชี้ ให้ดู gap_cm วิ่ง (เป็นค่าประมาณจากกล้อง)\n'
        + 'กดถัดไป: ถามคำถามบนจอ ยังไม่เฉลย บอกว่าจะกลับมาตอบตอนที่ 2\n'
        + 'ถ้ากล้องไม่ติด จอจะเข้าโหมดสาธิตเอง: A/S นิ้วมือซ้าย K/L นิ้วมือขวา ↑/↓ ระยะ (กดจากจอนี้ได้)\n'
        + 'ถ้ากล้องติดแต่จับมือได้ไม่ดี (ย้อนแสง) กด D เพื่อเข้าโหมดสาธิตทันที',
    },

    {
      kind: 'objectives',
      title: 'วัตถุประสงค์การเรียนรู้',
      notes: '⏱ 1:15–1:30\nอ่านหัวข้อสองข้อพอ ผู้เรียนจะรู้ว่าต้องตั้งใจตรงไหน',
      items: [
        {
          icon: 'code',
          head: 'ตัวแปรและการตั้งชื่อ',
          body: 'บอกความหมายของตัวแปร และกำหนดค่าให้ตัวแปรโดยตั้งชื่อตามกฎการตั้งชื่อได้',
        },
        {
          icon: 'cpu',
          head: 'ชนิดข้อมูลตัวเลข',
          body: 'บอกชนิดข้อมูลจำนวนเต็ม (int) และจำนวนทศนิยม (float) ให้เหมาะสมกับลักษณะของข้อมูลที่กำหนดให้ได้',
        },
      ],
    },

    {
      // A variable is a name bound to a value — not a box. One arrow press
      // per line: the tag ties to 25, then moves to 30; the third press
      // brings up what = means.
      kind: 'name-tag',
      part: 1,
      steps: 2,
      title: 'ตัวแปร คือป้ายชื่อที่ผูกกับค่า',
      name: 'score',
      binds: [
        { code: 'score = 25', value: 25, kind: 'int' },
        { code: 'score = 30', value: 30, kind: 'int' },
      ],
      rule: '= คือการผูกค่าทางขวาเข้ากับชื่อทางซ้าย ไม่ได้แปลว่า "เท่ากับ"',
      example: { code: 'total = left + right', note: 'คำนวณทางขวาก่อน แล้วจึงผูกผลลัพธ์กับชื่อทางซ้าย' },
      notes:
        '⏱ 1:30–2:30\n'
        + 'score = 25 คือแปะป้าย score ไว้ที่ค่า 25\n'
        + 'กดถัดไป: score = 30 ป้ายย้ายไปที่ 30 ส่วน 25 ไม่มีชื่อเรียกแล้ว\n'
        + 'กดถัดไป: เน้นว่า = ไม่ได้แปลว่าเท่ากับ ทางขวาคำนวณก่อนเสมอ (ยกตัวอย่าง total จากขั้นนำ)',
    },

    {
      kind: 'quiz',
      order: 0,
      part: 1,
      notes: '⏱ 2:30–3:20 ขั้นประเมิน (ข้อ 1)\nให้เวลา 20 วินาที ตอนเฉลย: ทางขวา score + 5 คิดก่อนจากค่าเดิม 25 ได้ 30 แล้วป้าย score ย้ายไปที่ 30',
    },

    {
      // Three rules, each the cause of a SyntaxError. The broken names here
      // are deliberately not the ones the tag-pick question asks about.
      // Error lines copied from Python 3.14.6.
      kind: 'name-rules',
      part: 1,
      title: 'ผิดกฎการตั้งชื่อข้อเดียว Python ก็ไม่ยอมรัน',
      rules: [
        { head: 'ใช้ได้เฉพาะตัวอักษร ตัวเลข และ _', bad: 'my score = 10', error: 'SyntaxError: invalid syntax', fix: 'my_score' },
        { head: 'ห้ามขึ้นต้นด้วยตัวเลข', bad: '1st_place = 1', error: 'SyntaxError: invalid decimal literal', fix: 'place1' },
        { head: 'ห้ามใช้คำสงวนของ Python', bad: 'class = 5', error: 'SyntaxError: invalid syntax', fix: 'class_no' },
      ],
      notes:
        '⏱ 3:20–4:00\n'
        + 'ข้อความ SyntaxError เป็นของจริงจาก Python ชื่อผิดกฎเมื่อไหร่ โปรแกรมไม่ยอมทำงานเลยสักบรรทัด\n'
        + 'คำสงวน เช่น if, for, while, class ใช้เป็นชื่อตัวแปรไม่ได้',
    },

    {
      kind: 'quiz',
      order: 1,
      part: 1,
      notes: '⏱ 4:00–5:00 ขั้นประเมิน (ข้อ 2)\nป้ายที่ผิดแต่ละอันผิดกฎคนละข้อ ตอนเฉลยดูตัวเลขใต้ป้าย ป้ายไหนตอบถูกน้อย ย้ำกฎข้อนั้น',
    },

    {
      // Part 2 opens on the camera again, now with each value's type —
      // the answer to the question the hook left open.
      kind: 'cam-tags',
      reveal: true,
      part: 2,
      title: 'กลับมาดูป้ายชื่อติดมือ',
      notes:
        '⏱ 5:00–5:30\n'
        + 'เปิดกล้องอีกครั้ง ชูนิ้วให้ดูว่า left กับ right เป็น int เพราะนับนิ้ว\n'
        + 'จีบนิ้วให้ดูว่า gap_cm เป็น float เพราะวัดระยะ ตอบคำถามที่ทิ้งไว้ตอนต้นคาบ',
    },

    {
      kind: 'count-measure',
      part: 2,
      title: 'นับได้ใช้ int วัดได้ใช้ float',
      count: { label: 'นิ้วที่ชูขึ้น นับทีละหนึ่ง ไม่มีครึ่งนิ้ว', name: 'fingers', value: 3 },
      measure: { label: 'ระยะห่างนิ้ว วัดได้ละเอียดลงไปเรื่อย ๆ', name: 'gap_cm', value: 4.73 },
      notes:
        '⏱ 5:30–6:00\n'
        + 'ถามห้องว่าของรอบตัวอะไรได้จากการนับ อะไรได้จากการวัด\n'
        + 'ถ้ามีคนถามเรื่องเงิน: ระบบจริงเก็บเป็นสตางค์ด้วย int หรือใช้ Decimal เพราะ float ปัดเศษ เช่น 0.1 + 0.2 ได้ 0.30000000000000004',
    },

    {
      kind: 'type-of',
      part: 2,
      steps: 1,
      title: 'ชนิดข้อมูลเป็นของค่า ไม่ใช่ของชื่อ',
      checks: [
        { literal: '5', kind: 'int' },
        { literal: '5.0', kind: 'float' },
      ],
      note: 'มีจุดทศนิยมคือ float แม้ไม่มีเศษ',
      name: 'x',
      moves: [
        { value: 3, kind: 'int' },
        { value: 4.73, kind: 'float' },
      ],
      notes:
        '⏱ 6:00–6:40\n'
        + 'type() ถามชนิดของค่า 5 กับ 5.0 ค่าเท่ากันแต่คนละชนิด\n'
        + 'กดถัดไป: ป้าย x ย้ายจาก 3 ไป 4.73 ชนิดเปลี่ยนตามค่า ไม่ได้ติดอยู่กับชื่อ',
    },

    {
      kind: 'quiz',
      order: 2,
      part: 2,
      notes: '⏱ 6:40–8:00 ขั้นประเมิน (ข้อ 3)\nให้เวลา 45 วินาที ตอนเฉลยถามทีละข้อว่าได้จากการนับหรือการวัด',
    },

    {
      kind: 'summary',
      title: 'สรุปบทเรียน',
      lines: [
        'ตัวแปรคือป้ายชื่อที่ผูกกับค่า และ = คือการผูกค่าทางขวาเข้ากับชื่อทางซ้าย',
        'ตั้งชื่อด้วยตัวอักษร ตัวเลข และ _ ห้ามขึ้นต้นด้วยตัวเลข และห้ามใช้คำสงวน',
        'ข้อมูลจากการนับใช้ int ข้อมูลจากการวัดใช้ float ตรวจด้วย type()',
      ],
      notes: '⏱ 8:00–8:50 ขั้นสรุป\nอ่านทีละบรรทัด ให้ผู้เรียนพูดตามคำสำคัญ: ป้ายชื่อ, กฎ 3 ข้อ, นับกับวัด\nปิดท้ายว่าวันนี้ทำได้ครบทั้งสองข้อแล้ว',
    },

    {
      kind: 'results',
      notes: '⏱ 8:50–9:20\nประกาศอันดับ ชมคนที่ตอบถูกและตอบเร็ว และให้ดาวกับคนที่มีส่วนร่วม',
    },
    {
      kind: 'outro',
      title: 'จบบทเรียน',
      body: 'ขอบคุณที่ตั้งใจเรียนวันนี้',
      notes: '⏱ 9:20–10:00 เวลาสำรอง\nเปิดให้ถามข้อสงสัยสั้น ๆ แล้วปิดคาบ',
      credit: 'การตรวจจับมือ: MediaPipe Hand Landmarker (Google)',
    },
  ],
};

const DECKS = { PYINIT, PYVAR, PYOP, PYVAR2 };

function genericDeck(questions) {
  const ordered = [...(questions || [])].sort((a, b) => a.order_index - b.order_index);
  return {
    meta: null,
    stops: [
      { kind: 'cover' },
      ...ordered.map((q) => ({ kind: 'quiz', order: q.order_index })),
      { kind: 'results' },
      { kind: 'outro', title: 'จบบทเรียน', body: 'ขอบคุณที่ตั้งใจเรียนวันนี้' },
    ],
  };
}

export function buildDeck(roomCode, questions) {
  const custom = DECKS[String(roomCode || '').toUpperCase()];
  if (custom) return custom;
  return genericDeck(questions);
}
